/**
 * Starter ActionIcon — wraps Mantine's `<ActionIcon>` with the design's
 * focus ring and a `tone="surface"` variant that matches the icon-button
 * preset (`.btn-icon`) from the design's web-app kit: transparent with a
 * 1px border that strengthens on hover.
 *
 * For most uses, the default Mantine variants (`filled` / `subtle` /
 * `outline` / `light` / `transparent` / `default`) work unchanged.
 */
import { forwardRef } from 'react';
import {
  ActionIcon as MantineActionIcon,
  type ActionIconProps as MantineActionIconProps,
  type PolymorphicComponentProps,
} from '@mantine/core';
import styles from './ActionIcon.module.css';

export interface ActionIconProps extends MantineActionIconProps {
  /**
   * Optional tone hint.
   * - `surface` switches to the design's bordered transparent style (use for
   *   top-bar toggles and card actions); has hover + active states.
   * - `destructive` keeps the bordered transparent base but shifts the icon
   *   and border to the error color on hover (use for delete affordances).
   */
  readonly tone?: 'surface' | 'destructive';
}

export const ActionIcon = forwardRef<
  HTMLButtonElement,
  PolymorphicComponentProps<'button', ActionIconProps>
>(function ActionIcon({ tone, variant, className, ...rest }, ref) {
  // Both bordered tones map onto Mantine's `default` variant; the visual
  // treatment is driven entirely by the `data-tone` attribute in CSS.
  const resolvedVariant =
    tone === 'surface' || tone === 'destructive' ? 'default' : variant;
  // Forward variant/tone only when defined to satisfy exactOptionalPropertyTypes.
  const variantProp =
    resolvedVariant !== undefined ? { variant: resolvedVariant } : {};
  const toneProp = tone !== undefined ? { 'data-tone': tone } : {};
  return (
    <MantineActionIcon
      ref={ref}
      {...variantProp}
      {...toneProp}
      classNames={{
        root: [styles.root, className].filter(Boolean).join(' '),
      }}
      {...rest}
    />
  );
});
